import type { MetaFunction } from "@remix-run/node";
import { json } from "@remix-run/node";
import type { LoaderFunctionArgs } from "@remix-run/node";
import { useLoaderData, Link } from "@remix-run/react";
import { getAuthUser } from "~/services/auth.server";
import { Button } from "~/components/ui/Button";

export const meta: MetaFunction = () => {
  return [
    { title: "서비스 소개 - 슬로우다이브" },
    {
      name: "description",
      content:
        "저속노화를 위한 맞춤 레시피 서비스 Slowdive의 서비스 소개입니다. 과학적으로 입증된 저속노화 식단으로 건강한 삶을 시작하세요.",
    },
  ];
};

export async function loader({ request }: LoaderFunctionArgs) {
  const user = await getAuthUser(request);

  return json({
    user,
  });
}

export default function AboutPage() {
  const { user } = useLoaderData<typeof loader>();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="flex mb-8" aria-label="Breadcrumb">
          <ol className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
            <li>
              <Link
                to="/"
                className="hover:text-gray-700 dark:hover:text-gray-300"
              >
                홈
              </Link>
            </li>
            <li>
              <span className="mx-2">/</span>
            </li>
            <li className="text-gray-900 dark:text-white font-medium">
              서비스 소개
            </li>
          </ol>
        </nav>

        {/* Hero Section - Service Purpose and Value */}
        <section className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden mb-12">
          <div className="px-8 sm:px-16 py-16 sm:py-24">
            <div className="max-w-4xl mx-auto">
              <div className="flex justify-center mb-8">
                <img
                  src="/logo-light.png"
                  alt="슬로우다이브 로고"
                  className="object-cover dark:hidden"
                />
                <img
                  src="/logo-dark.png"
                  alt="슬로우다이브 로고"
                  className="object-cover hidden dark:block"
                />
              </div>

              <div className=" space-y-12 text-lg leading-relaxed text-gray-800 dark:text-gray-200 text-center mb-16">
                <div className="space-y-6 font-serif">
                  <p>
                    빠른 것이 미덕이 된 시대.
                    <br />
                    우리는, 그 속도에서 한 걸음 물러서기로 했습니다.
                  </p>

                  <p className=" text-gray-600 dark:text-gray-400 text-xl">
                    "정말 두려운 건 나이 드는 게 아니라, 지쳐가는 게 아닐까?"
                  </p>

                  <p>
                    슬로우다이브는 노화를 거부하지 않습니다.
                    <br />
                    그저, 그 속도를 천천히 늦추며
                    <br />
                    몸과 마음의 리듬을 되찾고자 합니다.
                  </p>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 mt-8">
                  <p className="font-bold text-xl text-blue-900 dark:text-blue-100">
                    매일 한 끼, 더 나은 하루.
                    <br />
                    천천히, 그러나 확실하게.
                  </p>
                  <p className="italic text-blue-700 dark:text-blue-300 text-lg mt-2">
                    Slow down, dive into life.
                  </p>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                {!user ? (
                  <>
                    <Link to="/signup">
                      <Button size="lg" className="w-full sm:w-auto">
                        무료로 시작하기
                      </Button>
                    </Link>
                    <Link to="/recipes">
                      <Button
                        variant="outline"
                        size="lg"
                        className="w-full sm:w-auto"
                      >
                        레시피 둘러보기
                      </Button>
                    </Link>
                  </>
                ) : (
                  <>
                    <Link to="/recipes">
                      <Button size="lg" className="w-full sm:w-auto">
                        레시피 탐색하기
                      </Button>
                    </Link>
                    <Link to="/shopping/create">
                      <Button
                        variant="outline"
                        size="lg"
                        className="w-full sm:w-auto"
                      >
                        장보기 목록 만들기
                      </Button>
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Main Features Section */}
        <section className="mb-24">
          <div className="text-center my-24">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              슬로우다이브의 주요 기능
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              과학적으로 입증된 저속노화 식단을 바탕으로 한 맞춤형 레시피 서비스
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1: 저속노화 레시피 */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 text-center">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg
                  className="w-8 h-8 text-green-600 dark:text-green-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                저속노화 레시피
              </h3>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                과학적 연구를 바탕으로 한 항산화 성분이 풍부한 레시피로 건강한
                노화를 지원합니다.
              </p>
            </div>

            {/* Feature 2: 맞춤형 추천 */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 text-center">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg
                  className="w-8 h-8 text-blue-600 dark:text-blue-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                맞춤형 추천
              </h3>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                개인의 건강 상태와 선호도를 고려한 맞춤형 레시피 추천으로 더
                나은 식단을 제안합니다.
              </p>
            </div>

            {/* Feature 3: 영양 정보 */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 text-center">
              <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg
                  className="w-8 h-8 text-purple-600 dark:text-purple-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                상세한 영양 정보
              </h3>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                각 레시피의 영양성분과 저속노화 효과를 시각적으로 확인할 수
                있습니다.
              </p>
            </div>

            {/* Feature 4: 스마트 장보기 */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 text-center">
              <div className="w-16 h-16 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg
                  className="w-8 h-8 text-orange-600 dark:text-orange-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.293 2.293A1 1 0 005 16h12M7 13v4a2 2 0 002 2h4a2 2 0 002-2v-4m-6 2a2 2 0 11-4 0 2 2 0 014 0zm0 0a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                스마트 장보기
              </h3>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                선택한 레시피의 재료를 자동으로 정리하고 효율적인 장보기 목록을
                생성합니다.
              </p>
            </div>

            {/* Feature 5: 건강 정보 */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 text-center">
              <div className="w-16 h-16 bg-teal-100 dark:bg-teal-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg
                  className="w-8 h-8 text-teal-600 dark:text-teal-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                건강 정보 아카이브
              </h3>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                저속노화와 건강한 식단에 대한 전문적인 정보와 팁을 제공합니다.
              </p>
            </div>

            {/* Feature 6: 커뮤니티 */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 text-center">
              <div className="w-16 h-16 bg-pink-100 dark:bg-pink-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg
                  className="w-8 h-8 text-pink-600 dark:text-pink-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                레시피 평가 & 즐겨찾기
              </h3>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                좋아하는 레시피를 저장하고 평가하여 나만의 건강한 레시피
                컬렉션을 만들어보세요.
              </p>
            </div>
          </div>
        </section>

        {/* Service Philosophy Section */}
        <section className="bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-900/20 dark:to-green-900/20 rounded-lg p-8 sm:p-12 mb-12">
          <div className="max-w-4xl mx-auto text-center my-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
              우리의 철학
            </h2>
            <div className="space-y-6 text-lg text-gray-700 dark:text-gray-300">
              <p>
                과학적으로 입증된 저속노화 식단을 바탕으로,
                <br />
                삶에 온기를 더하는 식탁을 제안합니다.
              </p>
              <p>
                이는 결핍을 강요하는 다이어트도,
                <br />
                불편을 감수해야 하는 식이요법도 아닙니다.
              </p>
              <p className="font-semibold text-xl text-blue-900 dark:text-blue-100">
                자연에 가까운 식재료, 균형 잡힌 조리,
                <br />
                그리고 따뜻한 식사 한 끼의 힘을 믿습니다.
              </p>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 sm:p-12 text-center">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            지금 시작해보세요
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-8 max-w-2xl mx-auto">
            건강한 노화를 위한 첫 걸음을 슬로우다이브와 함께 시작하세요.
            과학적으로 검증된 레시피로 더 나은 내일을 만들어가세요.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            {!user ? (
              <>
                <Link to="/signup">
                  <Button size="xl" className="w-full sm:w-auto">
                    무료 회원가입
                  </Button>
                </Link>
                <Link to="/articles">
                  <Button
                    variant="outline"
                    size="xl"
                    className="w-full sm:w-auto"
                  >
                    건강 정보 보기
                  </Button>
                </Link>
              </>
            ) : (
              <>
                <Link to="/recommendations">
                  <Button size="xl" className="w-full sm:w-auto">
                    {/* <svg
                      className="w-5 h-5 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                      />
                    </svg> */}
                    맞춤 레시피 추천받기
                  </Button>
                </Link>
                <Link to="/favorites">
                  <Button
                    variant="outline"
                    size="xl"
                    className="w-full sm:w-auto"
                  >
                    내 즐겨찾기 보기
                  </Button>
                </Link>
              </>
            )}
          </div>

          <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              궁금한 점이 있으시다면{" "}
              <Link
                to="/articles"
                className="text-blue-600 dark:text-blue-400 hover:underline"
              >
                건강 정보 섹션
              </Link>
              을 확인해보세요.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}
